
import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiUser, FiMail, FiPhone, FiLock, FiShield, FiRefreshCw, FiUsers, FiUserCheck, FiUserX, FiGlobe } from 'react-icons/fi';
import { authAPI, userAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  
  // Formulaire de création
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    role_id: ''
  });

  // Formulaire d'édition
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role_id: ''
  });

  // Erreurs de formulaire
  const [errors, setErrors] = useState({});

  // Calculer les statistiques
  const totalUsers = users.length;
  const adminCount = users.filter(u => u.role?.name === 'admin').length;
  const subAdminCount = users.filter(u => u.role?.name === 'sous_admin').length;
  const managerCount = users.filter(u => u.role?.name === 'operationnel').length;

  // Charger les données
  useEffect(() => {
    fetchAllData();
  }, []);

  // Appliquer les filtres
  useEffect(() => {
    applyFilters();
  }, [searchTerm, selectedRole, users]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchUsers(), fetchRoles()]);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      alert('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      console.log('Récupération utilisateurs');
      const response = await userAPI.getAll();
      console.log('Récupération utilisateurs:', response);
      if (response.data?.success) {
        // Filtrer les utilisateurs non-clients
        const nonClientUsers = response.data.data.filter(user => 
          user.role && user.role.name !== 'client'
        );
        setUsers(nonClientUsers);
        setFilteredUsers(nonClientUsers);
      } else {
        setUsers([]);
        setFilteredUsers([]);
      }
    } catch (error) {
      console.error('Erreur récupération utilisateurs:', error);
      alert('Erreur récupération utilisateurs: ' + (error.response?.data?.message || error.message));
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await userAPI.getRoles();
      if (response.data?.success) {
        // Filtrer les rôles (exclure "client" si nécessaire)
        const filteredRoles = response.data.data.filter(role => 
          role.name !== 'client'
        );
        setRoles(filteredRoles);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error('Erreur récupération rôles:', error);
      alert('Erreur récupération rôles: ' + (error.response?.data?.message || error.message));
    }
  };

  const applyFilters = () => {
    let result = users;

    // Filtre par recherche
    if (searchTerm) {
      result = result.filter(user => {
        const nameMatch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
        const emailMatch = user.email?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
        const phoneMatch = user.phone?.includes(searchTerm) || false;
        return nameMatch || emailMatch || phoneMatch;
      });
    }

    // Filtre par rôle
    if (selectedRole) {
      result = result.filter(user => user.role_id?.toString() === selectedRole);
    }

    setFilteredUsers(result);
  };

  const handleInputChange = (e, isEdit = false) => {
    const { name, value } = e.target;
    if (isEdit) {
      setEditFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    // Effacer l'erreur du champ
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = (isEdit = false) => {
    const data = isEdit ? editFormData : formData;
    const newErrors = {};

    if (!data.name?.trim()) newErrors.name = 'Le nom est obligatoire';
    if (!data.email?.trim()) newErrors.email = 'L\'email est obligatoire';
    if (!isEdit && !data.password?.trim()) newErrors.password = 'Le mot de passe est obligatoire';
    if (!isEdit && !data.password_confirmation?.trim()) newErrors.password_confirmation = 'La confirmation est obligatoire';
    if (!isEdit && data.password !== data.password_confirmation) newErrors.password_confirmation = 'Les mots de passe ne correspondent pas';
    if (!data.role_id) newErrors.role_id = 'Le rôle est obligatoire';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserLoading(true);
    setErrors({});

    if (!validateForm(false)) {
      setUserLoading(false);
      return;
    }

    try {
      const userData = {
        ...formData,
        phone: formData.phone || null
      };

      console.log('Création utilisateur:', userData);
      const response = await authAPI.register(userData);
      console.log('Création utilisateur:', response);

      if (response.success) {
        alert('✅ Utilisateur créé avec succès!');
        closeModal();
        fetchUsers();
      } else {
        throw new Error(response.data?.message || 'Erreur inconnue');
      }
    } catch (error) {
      console.error('Erreur création utilisateur:', error);
      
      // Gestion des erreurs de validation Laravel
      if (error.response?.data?.errors) {
        const validationErrors = {};
        Object.entries(error.response.data.errors).forEach(([field, messages]) => {
          validationErrors[field] = messages.join(', ');
        });
        setErrors(validationErrors);
        alert('❌ Veuillez corriger les erreurs dans le formulaire');
      } else {
        alert('❌ Erreur: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setUserLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setUserLoading(true);
    setErrors({});

    if (!validateForm(true)) {
      setUserLoading(false);
      return;
    }

    try {
      const userData = {
        name: editFormData.name,
        email: editFormData.email,
        phone: editFormData.phone || null,
        role_id: editFormData.role_id
      };

      console.log('Mise à jour utilisateur:', editingUser.id, userData);
      const response = await userAPI.update(editingUser.id, userData);

      if (response.data?.success) {
        console.log('✅ Utilisateur modifié avec succès!');
        closeEditModal();
        fetchUsers();
      } else {
        throw new Error(response.data?.message || 'Erreur inconnue');
      }
    } catch (error) {
      console.error('Erreur modification utilisateur:', error);
      
      if (error.response?.data?.errors) {
        const validationErrors = {};
        Object.entries(error.response.data.errors).forEach(([field, messages]) => {
          validationErrors[field] = messages.join(', ');
        });
        setErrors(validationErrors);
        alert('❌ Veuillez corriger les erreurs dans le formulaire');
      } else {
        alert('❌ Erreur: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setUserLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (userId === currentUser?.id) {
      alert('❌ Vous ne pouvez pas supprimer votre propre compte');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${userName}"?`)) {
      return;
    }

    try {
      await userAPI.delete(userId);
      alert('✅ Utilisateur supprimé avec succès!');
      fetchUsers();
    } catch (error) {
      console.error('Erreur suppression utilisateur:', error);
      alert('❌ Erreur: ' + (error.response?.data?.message || error.message));
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
      role_id: ''
    });
    setErrors({});
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setErrors({});
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      role_id: user.role_id || ''
    });
    setErrors({});
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
    setErrors({});
  };

  const getRoleColor = (roleName) => {
    switch (roleName?.toLowerCase()) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'sous_admin':
        return 'bg-blue-100 text-blue-800';
      case 'gestionnaire':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="pb-6">
      {/* En-tête compact pour mobile */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">Gestion des Utilisateurs</h1>
            <p className="text-gray-600 text-sm md:text-base">
              {totalUsers} utilisateur{totalUsers !== 1 ? 's' : ''} • {roles.length} rôle{roles.length !== 1 ? 's' : ''}
            </p>
          </div>
          
          <div className="flex gap-3">
            {/* Bouton actualisation mobile */}
            <button
              onClick={fetchAllData}
              className="md:hidden p-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              title="Actualiser"
            >
              <FiRefreshCw className="text-lg" />
            </button>
            
            <button
              onClick={openModal}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm md:text-base flex-1 md:flex-none"
            >
              <FiPlus className="text-base" />
              <span className="hidden xs:inline">Nouvel utilisateur</span>
              <span className="xs:hidden">Nouveau</span>
            </button>
          </div>
        </div>

        {/* Bouton actualisation desktop */}
        <button
          onClick={fetchAllData}
          className="hidden md:flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium text-sm mb-6"
        >
          <FiRefreshCw />
          <span>Actualiser les données</span>
        </button>
      </div>

      {/* Statistiques compactes pour mobile */}
      <div className="grid grid-cols-2 xs:grid-cols-4 gap-3 md:gap-4 mb-6">
        {/* Total utilisateurs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-50 rounded-lg">
              <FiUsers className="text-orange-600 text-lg" />
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-bold text-gray-800 mb-0.5">
                {totalUsers}
              </p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
          </div>
        </div>

        {/* Administrateurs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <FiShield className="text-purple-600 text-lg" />
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-bold text-gray-800 mb-0.5">
                {adminCount}
              </p>
              <p className="text-xs text-gray-500">Admins</p>
            </div>
          </div>
        </div>

        {/* Sous-admins */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <FiUserCheck className="text-blue-600 text-lg" />
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-bold text-gray-800 mb-0.5">
                {subAdminCount}
              </p>
              <p className="text-xs text-gray-500">Sous-admins</p>
            </div>
          </div>
        </div>

        {/* Gestionnaires */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <FiGlobe className="text-green-600 text-lg" />
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-bold text-gray-800 mb-0.5">
                {managerCount}
              </p>
              <p className="text-xs text-gray-500">Operationnels</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-purple-100 rounded-lg">
            <FiSearch className="text-purple-600 text-lg" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-800 text-sm md:text-base">Recherche et filtres</h2>
            <p className="text-xs text-gray-500">{filteredUsers.length} résultat{filteredUsers.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Recherche */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Recherche
            </label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-base" />
              <input
                type="text"
                placeholder="Nom, email, téléphone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
              />
            </div>
          </div>

          {/* Filtre par rôle */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Filtre par rôle
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm md:text-base"
            >
              <option value="">Tous les rôles</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Bouton reset filtres */}
        {(searchTerm || selectedRole) && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedRole('');
              }}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
            >
              <FiX className="text-xs" />
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </div>

      {/* Tableau des utilisateurs - Optimisé pour mobile */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm md:text-base">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Utilisateur
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hidden sm:table-cell">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Rôle
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="bg-purple-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                          <FiUser className="text-purple-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-900 text-sm md:text-base truncate">
                            {user.name}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {user.email}
                          </div>
                          {/* Contact mobile */}
                          <div className="sm:hidden mt-1">
                            {user.phone && (
                              <div className="flex items-center gap-1 text-xs text-gray-600">
                                <FiPhone className="text-gray-400" />
                                <span>{user.phone}</span>
                              </div>
                            )}
                            <div className="text-xs text-gray-400 mt-0.5">
                              Inscrit le {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <FiMail className="text-gray-400 text-sm" />
                          <span className="text-sm">{user.email}</span>
                        </div>
                        {user.phone && (
                          <div className="flex items-center gap-2">
                            <FiPhone className="text-gray-400 text-sm" />
                            <span className="text-sm">{user.phone}</span>
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-2">
                          Inscrit le {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role?.name)}`}>
                        {user.role?.name || 'Non défini'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1 md:gap-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-1.5 md:p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <FiEdit2 className="text-base md:text-lg" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id, user.name)}
                          disabled={user.id === currentUser?.id}
                          className={`p-1.5 md:p-2 rounded-lg transition-colors ${
                            user.id === currentUser?.id
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-red-600 hover:bg-red-50'
                          }`}
                          title={user.id === currentUser?.id ? "Vous ne pouvez pas vous supprimer" : "Supprimer"}
                        >
                          <FiTrash2 className="text-base md:text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-4 py-8 md:py-12 text-center">
                    <div className="flex flex-col items-center text-gray-500">
                      <FiUser className="text-3xl md:text-4xl text-gray-300 mb-3" />
                      <p className="text-base md:text-lg font-medium mb-2">Aucun utilisateur trouvé</p>
                      <p className="text-xs md:text-sm mb-4 text-center max-w-md">
                        {searchTerm || selectedRole 
                          ? 'Aucun résultat pour vos filtres' 
                          : 'Commencez par ajouter un utilisateur'}
                      </p>
                      {!searchTerm && !selectedRole && (
                        <button 
                          onClick={openModal}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm md:text-base"
                        >
                          Ajouter un utilisateur
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Résumé statistique pour mobile */}
      <div className="lg:hidden mt-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 md:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-500 rounded-lg">
            <FiUsers className="text-white text-lg" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">Distribution des rôles</h3>
            <p className="text-sm text-gray-600">{totalUsers} utilisateurs au total</p>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/80 rounded-lg p-3 text-center">
            <p className="text-xs text-purple-600 mb-1">Admins</p>
            <p className="text-lg font-bold text-gray-800">{adminCount}</p>
          </div>
          <div className="bg-white/80 rounded-lg p-3 text-center">
            <p className="text-xs text-blue-600 mb-1">Sous-admins</p>
            <p className="text-lg font-bold text-gray-800">{subAdminCount}</p>
          </div>
          <div className="bg-white/80 rounded-lg p-3 text-center">
            <p className="text-xs text-green-600 mb-1">Operationnels</p>
            <p className="text-lg font-bold text-gray-800">{managerCount}</p>
          </div>
        </div>
      </div>

      {/* Les modals restent inchangés */}
      {/* Modal de création */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 md:p-6 border-b">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">Nouvel utilisateur</h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-full">
                <FiX className="text-xl md:text-2xl text-gray-600" />
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="p-4 md:p-6 space-y-4">
              {/* ... reste du formulaire ... */}
              <div>
                 <label className="block text-sm font-semibold text-gray-700 mb-2">
                   Nom complet *
                 </label>
                 <input
                   type="text"
                   name="name"
                   value={formData.name}
                   onChange={handleInputChange}
                   className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                     errors.name ? 'border-red-500' : 'border-gray-300'
                   }`}
                   placeholder="Ex: Jean Dupont"
                 />
                 {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
               </div>

               <div>
                 <label className="block text-sm font-semibold text-gray-700 mb-2">
                   Email *
                 </label>
                 <input
                   type="email"
                   name="email"
                   value={formData.email}
                   onChange={handleInputChange}
                   className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                     errors.email ? 'border-red-500' : 'border-gray-300'
                   }`}
                   placeholder="exemple@email.com"
                 />
                 {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
               </div>

               <div>
                 <label className="block text-sm font-semibold text-gray-700 mb-2">
                   Téléphone
                 </label>
                 <input
                   type="tel"
                   name="phone"
                   value={formData.phone}
                   onChange={handleInputChange}
                   className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                   placeholder="Ex: 6XX XX XX XX"
                 />
               </div>

               <div>
                 <label className="block text-sm font-semibold text-gray-700 mb-2">
                   Rôle *
                 </label>
                 <select
                   name="role_id"
                   value={formData.role_id}
                   onChange={handleInputChange}
                   className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                     errors.role_id ? 'border-red-500' : 'border-gray-300'
                   }`}
                 >
                   <option value="">Sélectionner un rôle</option>
                   {roles.map((role) => (
                     <option key={role.id} value={role.id}>
                       {role.name}
                     </option>
                   ))}
                 </select>
                 {errors.role_id && <p className="text-red-500 text-xs mt-1">{errors.role_id}</p>}
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div>
                   <label className="block text-sm font-semibold text-gray-700 mb-2">
                     Mot de passe *
                   </label>
                   <div className="relative">
                     <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                     <input
                       type="password"
                       name="password"
                       value={formData.password}
                       onChange={handleInputChange}
                       className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                         errors.password ? 'border-red-500' : 'border-gray-300'
                       }`}
                       placeholder="••••••••"
                     />
                   </div>
                   {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                 </div>

                 <div>
                   <label className="block text-sm font-semibold text-gray-700 mb-2">
                     Confirmer *
                   </label>
                   <div className="relative">
                     <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                     <input
                       type="password"
                       name="password_confirmation"
                       value={formData.password_confirmation}
                       onChange={handleInputChange}
                       className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                         errors.password_confirmation ? 'border-red-500' : 'border-gray-300'
                       }`}
                       placeholder="••••••••"
                     />
                   </div>
                   {errors.password_confirmation && (
                     <p className="text-red-500 text-xs mt-1">{errors.password_confirmation}</p>
                   )}
                 </div>
               </div>

               <div className="flex gap-4 pt-4 border-t">
                 <button
                   type="button"
                   onClick={closeModal}
                   disabled={userLoading}
                   className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
                 >
                   Annuler
                 </button>
                 <button
                   type="submit"
                   disabled={userLoading}
                   className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                 >
                   {userLoading ? (
                     <>
                       <FiRefreshCw className="animate-spin mr-2" />
                       Création...
                     </>
                   ) : (
                     'Créer l\'utilisateur'
                   )}
                 </button>
               </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'édition */}
      {isEditModalOpen && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 md:p-6 border-b">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">Modifier l'utilisateur</h2>
              <button onClick={closeEditModal} className="p-2 hover:bg-gray-100 rounded-full">
                <FiX className="text-xl md:text-2xl text-gray-600" />
              </button>
            </div>

            <form onSubmit={handleUpdateUser} className="p-4 md:p-6 space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-600">
                  Modification de: <strong>{editingUser.name}</strong>
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nom complet *
                </label>
                <input
                  type="text"
                  name="name"
                  value={editFormData.name}
                  onChange={(e) => handleInputChange(e, true)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={editFormData.email}
                  onChange={(e) => handleInputChange(e, true)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={editFormData.phone}
                  onChange={(e) => handleInputChange(e, true)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Rôle *
                </label>
                <select
                  name="role_id"
                  value={editFormData.role_id}
                  onChange={(e) => handleInputChange(e, true)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    errors.role_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Sélectionner un rôle</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {errors.role_id && <p className="text-red-500 text-xs mt-1">{errors.role_id}</p>}
              </div>

              <div className="flex gap-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeEditModal}
                  disabled={userLoading}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={userLoading}
                  className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {userLoading ? (
                    <>
                      <FiRefreshCw className="animate-spin mr-2" />
                      Modification...
                    </>
                  ) : (
                    'Modifier'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;